import { SignOffRequirement } from '@mnspro-cloud/services';
import { SignOffsResponse } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { find } from 'lodash';
import moment from 'moment-timezone';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirmationDialog } from '../../hooks';
import { SignOffButton } from '../SignOffButton/SignOffButton';
import {
  SignOffPerson,
  SignOffState,
} from '../SignOffButton/SignOffButton.interfaces';
import { SignOffLevel, SignOffPendingUpdate } from './LessonSignOff.interface';
import './LessonSignOff.scss';
import {
  determineSignOffState,
  getSigningPersons,
  isAllSigningRequired,
  isLessonSignable,
  someBodyAboveSigned,
} from './LessonSignOff.utils';

export interface LessonSignOffProps {
  lessonEnd: Date;
  schoolManagementSignOff?: SignOffsResponse;
  gradeSignOff?: SignOffsResponse;
  classSignOff?: SignOffsResponse;
  selfAzureId?: string;
  onSignClick?: (signOffLevel: SignOffLevel) => void;
  onUnsignClick?: (signOffLevel: SignOffLevel) => void;
  pendingSignOffUpdates?: SignOffPendingUpdate[];
  isMultiLessonsSigning?: boolean;
}

export const LessonSignOff: React.FC<LessonSignOffProps> = props => {
  const {
    gradeSignOff,
    classSignOff,
    schoolManagementSignOff,
    selfAzureId,
    onSignClick,
    onUnsignClick,
    pendingSignOffUpdates,
    lessonEnd: lessonStart,
    isMultiLessonsSigning,
  } = props;
  const { t } = useTranslation();
  const { getConfirmation } = useConfirmationDialog();
  const getSignOffButtonLabelPrefix = useCallback(
    (level: SignOffLevel) => {
      switch (level) {
        case SignOffLevel.Class:
          return t('classbook:lesson_sign_off:sign_off_button_prefix:class_management', { defaultValue: 'Klassenlehrer:in' });
        case SignOffLevel.Grade:
          return t('classbook:lesson_sign_off:sign_off_button_prefix:grade_management', { defaultValue: 'Stufenleitung' });
        case SignOffLevel.SchoolManagement:
          return t('classbook:lesson_sign_off:sign_off_button_prefix:school_management', { defaultValue: 'Schulleitung' });
      }
    },
    [t],
  );

  const getDesignatedSignersFromLevel = useCallback(
    (level: SignOffLevel): SignOffPerson[] | undefined => {
      let designatedSigners: SignOffPerson[] | undefined;
      switch (level) {
        case SignOffLevel.Class:
          if (classSignOff && isAllSigningRequired(classSignOff)) {
            designatedSigners = getSigningPersons(classSignOff);
          }
          break;
        case SignOffLevel.Grade:
          if (gradeSignOff && isAllSigningRequired(gradeSignOff)) {
            designatedSigners = getSigningPersons(gradeSignOff);
          }
          break;
        case SignOffLevel.SchoolManagement:
          if (
            schoolManagementSignOff &&
            isAllSigningRequired(schoolManagementSignOff)
          ) {
            designatedSigners = getSigningPersons(schoolManagementSignOff);
          }
          break;
      }

      return designatedSigners;
    },
    [classSignOff, gradeSignOff, schoolManagementSignOff],
  );
  const getSignOffButton = useCallback(
    (
      level: SignOffLevel,
      self: string | undefined,
      signOffs: SignOffsResponse | undefined,
      aboveSigned: boolean,
    ) => {
      if (
        signOffs === undefined ||
        signOffs.current.signOffRequirement === SignOffRequirement.none
      ) {
        return <></>;
      }
      const signOffState: SignOffState = determineSignOffState(
        signOffs.current,
      );

      const isSelf =
        self !== undefined &&
        signOffs.current.signatures.some(
          signOff => signOff.signer?.id === self,
        );

      // Persons that signed the lesson
      const doneSignatures = signOffs.current.signatures.filter(
        signOff => !!signOff.signedAt,
      );

      const mySignatureIsDone = !!doneSignatures.find(
        signOff => signOff.signer?.id === self && !!signOff.signedAt,
      );

      const isLoading =
        !!find(pendingSignOffUpdates, { level }) || isMultiLessonsSigning;

      const outlined = !onSignClick || !isSelf;

      return (
        <SignOffButton
          onClick={async () => {
            if (
              signOffState === SignOffState.NOT_SIGNED_OFF &&
              isSelf &&
              !mySignatureIsDone
            ) {
              onSignClick?.(level);
              return;
            }
            if (isSelf && mySignatureIsDone && !aboveSigned && onUnsignClick) {
              const shouldChange = await getConfirmation({
                headerText: t('classbook:lesson:signoff:design_header', { defaultValue: 'Abzeichnen rückgängig machen?' }),
                content: t('classbook:lesson:signoff:design_content', { defaultValue: 'Sind Sie sicher dass Sie die Abzeichnung rückgängig machen möchten?' }),
                cancelButtonText: t('cancel', { defaultValue: 'Abbrechen' }),
                confirmButtonText: t('yes', { defaultValue: 'Ja' }),
                showCloseButton: true,
              });

              if (shouldChange) {
                onUnsignClick?.(level);
              }
            }
            return undefined;
          }}
          signOffState={signOffState} //SignOffState.CONFIGURATION_ISSUE
          meta={getSignOffButtonLabelPrefix(level)}
          outline={outlined}
          date={
            doneSignatures === undefined || doneSignatures.length === 0
              ? undefined
              : moment
                  .max(
                    doneSignatures.map(signature =>
                      moment(signature.signedAt ?? ''),
                    ),
                  )
                  .format('LL')
          }
          isLoading={isLoading}
          personSignings={getDesignatedSignersFromLevel(level)}
        />
      );
    },
    [
      pendingSignOffUpdates,
      onSignClick,
      getSignOffButtonLabelPrefix,
      getDesignatedSignersFromLevel,
    ],
  );

  const lessonIsSignable = isLessonSignable(new Date(), lessonStart);

  const aboveGrade = schoolManagementSignOff
    ? someBodyAboveSigned([schoolManagementSignOff])
    : false;
  const aboveClass =
    aboveGrade || (gradeSignOff ? someBodyAboveSigned([gradeSignOff]) : false);

  return lessonIsSignable ? (
    <div className="flex justify-end w-full lessonSignOff">
      {getSignOffButton(
        SignOffLevel.SchoolManagement,
        selfAzureId,
        schoolManagementSignOff,
        false,
      )}
      {getSignOffButton(
        SignOffLevel.Grade,
        selfAzureId,
        gradeSignOff,
        aboveGrade,
      )}
      {getSignOffButton(
        SignOffLevel.Class,
        selfAzureId,
        classSignOff,
        aboveClass,
      )}
    </div>
  ) : (
    <></>
  );
};
